import React, { useState, useMemo, useRef, useEffect } from "react";
import { InfoWindow, Marker,Polygon } from "@react-google-maps/api";
import { Calendar } from "lucide-react";
import { format } from "date-fns";
import kMeans from "../../../utils/clusterMarker";
import GrievanceStatsPanel from "./GreivanceStatsPanel";
import GrievanceSummary from "./GrievanceSummary";
import {AlignLeft, AlignRight} from 'lucide-react'
import { Button, Tooltip } from "antd";

// Custom Card Components
const Card = ({ children, className = "" }) => (
  <div className={`${className}`}>{children}</div>
);

const CardContent = ({ children, className = "" }) => (
  <div className={`p-2 ${className}`}>{children}</div>
);

const GmsFullScreenMap = ({ mapRef, grievances, polygonPaths}) => {
    const [activeCluster, setActiveCluster] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedGrievanceId, setSelectedGrievanceId] = useState(null);
    const [selectedCategory, setSelectedCategory] = useState(""); 
    const [selectedGrievance, setSelectedGrievance] = useState(null);
    const [showStatsPanel, setShowStatsPanel] = useState(true);
    const cardRefs = useRef({});

  const getDynamicK = (pointsCount, zoom) => {
    return Math.max(20, Math.floor(pointsCount / 5));
  };
  const clusters = useMemo(() => {
    if (!grievances || grievances.length === 0) return [];

    const points = grievances.map((g) => ({
      lat: g.geometry.coordinates[1],
      lng: g.geometry.coordinates[0],
      grievance: g,
    }));

    const zoom = mapRef.current?.getZoom() || 8;
    const k = getDynamicK(points.length, zoom);
    const { centroids, assignments } = kMeans(points, k);

    return centroids.map((c, idx) => {
      const clusterGrievances = points.filter((_, i) => assignments[i] === idx);
      return { ...c, grievances: clusterGrievances };
    });
  }, [grievances]);
  const onClusterClick = (cluster) => {
    setActiveCluster(cluster);
    setCurrentPage(1); // reset pagination
    setSelectedGrievanceId(null);
    const markerLatLng = new window.google.maps.LatLng(cluster.lat, cluster.lng);
    mapRef.current?.panTo(markerLatLng);
    mapRef.current?.setZoom((mapRef.current?.getZoom() || 8) + 2); // zoom in a little
  };

  const onClosePanel = () => {
    setActiveCluster(null);
    setCurrentPage(1);
    setSelectedGrievanceId(null);
    mapRef.current?.setZoom((mapRef.current?.getZoom() || 8) - 2);
  };

  const getPriorityColor = (priority = "") => {
    switch (priority.toLowerCase()) {
      case "high":
        return "bg-red-100 text-red-800";
      case "medium":
        return "bg-yellow-100 text-yellow-800";
      case "low":
        return "bg-green-100 text-green-800";
      default:
        return "bg-gray-100 text-gray-800";
    }
  };

  const getPriorityIcon = (priority = "") => {
    switch (priority.toLowerCase()) {
        case "high":
        return "http://maps.google.com/mapfiles/ms/icons/red-dot.png";
        case "medium":
        return "http://maps.google.com/mapfiles/ms/icons/yellow-dot.png";
        case "low":
        return "http://maps.google.com/mapfiles/ms/icons/green-dot.png";
        default:
        return "http://maps.google.com/mapfiles/ms/icons/blue-dot.png";
    }
    };

   const getClusterIcon = (count, size = 40, color = "#004877") => {
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="${size}" height="${size}" viewBox="0 0 ${size} ${size}">
      <circle cx="${size / 2}" cy="${size / 2}" r="${size / 2 - 2}" fill="${color}" stroke="white" stroke-width="3"/>
      <text x="50%" y="55%" text-anchor="middle" fill="white" font-size="14" font-family="Arial" dy=".3em">${count}</text>
    </svg>`;
  
  return {
    url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
    scaledSize: new window.google.maps.Size(size, size),
    anchor: new window.google.maps.Point(size / 2, size / 2),
  };
};

  const pageSize = 3;
  const totalPages = activeCluster
    ? Math.ceil(activeCluster.grievances.length / pageSize)
    : 0;

  const paginatedGrievances = activeCluster
    ? activeCluster.grievances.slice(
        (currentPage - 1) * pageSize,
        currentPage * pageSize
      )
    : [];

  // Auto-scroll selected card into view
//   useEffect(() => {
//   if (selectedGrievanceId && cardRefs.current[selectedGrievanceId]) {
//     cardRefs.current[selectedGrievanceId].scrollIntoView({
//       behavior: "smooth",
//       block: "center",
//     });
//   }
// }, [selectedGrievanceId]);

useEffect(() => {
  if (activeCluster && selectedGrievanceId) {
    // find index of grievance in active cluster
    const idx = activeCluster.grievances.findIndex(
      (g) => g.grievance?.properties?.g_id === selectedGrievanceId
    );

    if (idx !== -1) {
      const newPage = Math.floor(idx / pageSize) + 1;

      // if grievance is not on current page, switch page
      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }

      // Scroll into view after pagination update
      setTimeout(() => {
        if (cardRefs.current[selectedGrievanceId]) {
          cardRefs.current[selectedGrievanceId].scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 200); // delay ensures DOM is updated
    }
  }
}, [selectedGrievanceId, activeCluster, pageSize]);

const filteredGrievances = useMemo(() => {
    if (!selectedCategory) return grievances;
    return grievances.filter(
      (g) => g?.properties?.cat_name === selectedCategory
    );
  }, [grievances, selectedCategory]);

  // Auto-scroll selected card into view
  useEffect(() => {
    if (selectedGrievanceId && cardRefs.current[selectedGrievanceId]) {
      cardRefs.current[selectedGrievanceId].scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [selectedGrievanceId]);

if (!grievances || grievances.length === 0) {
  return <div>No grievances found</div>;
}
  return (
    <>
    {polygonPaths?.map((polygon, idx) => (
        <Polygon
          key={idx}
          paths={polygon.coordinate.map(pt => ({
            lat: Number(pt.lat),
            lng: Number(pt.lng),
          }))}
          options={{
            strokeColor: "#810FCB",
            strokeOpacity: 1.0,
            strokeWeight: 2,
            fillColor: "#810FCB",
            fillOpacity: 0.5,
          }}
        />
      ))}
      {/* Markers */}
      {!activeCluster && selectedCategory==="" &&
        clusters.map((cluster, idx) => {
        if(cluster.length!==0){

        return (
          <Marker
            key={`cluster-${idx}`}
            position={{ lat: cluster.lat, lng: cluster.lng }}
            // icon={{
            //   url: "http://maps.google.com/mapfiles/ms/icons/red-dot.png",
            //   scaledSize: new window.google.maps.Size(40, 40),
            //   anchor: new window.google.maps.Point(20, 40),
            // }}
           icon={getClusterIcon(cluster.grievances.length, 40, "#004877")}
            onClick={() => onClusterClick(cluster)}
          />
        )}})}
    
      {activeCluster &&
        activeCluster.grievances.map((point, idx) => {
          const g = point.grievance?.properties || {};
          return (
            <Marker
              key={`grievance-${g.g_id || idx}`}
              position={{ lat: point.lat, lng: point.lng }}
              icon={{
                // url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
                url: getPriorityIcon(g.priority), 
                scaledSize: new window.google.maps.Size(30, 30),
                anchor: new window.google.maps.Point(15, 30),
              }}
              onClick={() => setSelectedGrievanceId(g.g_id)}
            />
          );
        })}

        {/* Show grievance markers when category filter is active */}
      {!activeCluster && selectedCategory !== "" &&
        filteredGrievances.map((g, idx) => (
          <Marker
            key={`filtered-${g.properties.g_id || idx}`}
            position={{
              lat: g.geometry.coordinates[1],
              lng: g.geometry.coordinates[0],
            }}
            icon={{
            //   url: "http://maps.google.com/mapfiles/ms/icons/blue-dot.png",
            url: getPriorityIcon(g.properties.priority), 
              scaledSize: new window.google.maps.Size(30, 30),
              anchor: new window.google.maps.Point(15, 30),
            }}
            // onClick={() => setSelectedGrievanceId(g.properties.g_id)}
            onClick={() => setSelectedGrievance(g)}
          />
        ))}

      {/* Right Side Panel */}
      {activeCluster && (
        <div className="absolute top-16 right-4 bg-white/50 backdrop-blur-xl border border-white/40 rounded-3xl shadow-lg w-[420px] max-h-[90vh] overflow-hidden z-[9] flex flex-col">
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b rounded-t-3xl">
            <h3 className="text-lg font-bold text-[#004877]">
              {activeCluster.grievances.length} Grievances
            </h3>
            <button
              onClick={onClosePanel}
              className="text-gray-600 hover:text-red-500 text-lg font-bold"
            >
              ✕
            </button>
          </div>

          {/* List */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {paginatedGrievances.map((item, i) => {
              const g = item.grievance?.properties || {};
              const isSelected = selectedGrievanceId === g.g_id;
              return (
                <Card
                  key={g.g_id || i}
                  ref={(el) => (cardRefs.current[g.g_id] = el)}
                  className={`shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                    transition-all duration-300 transform 
                    hover:shadow-[0_6px_24px_0_rgba(31,38,135,0.25)] 
                    relative overflow-hidden rounded-3xl border backdrop-blur-3xl
                    ${
                      isSelected
                        ? "border-blue-500 ring-2 ring-blue-300"
                        : "border-white/30"
                    }
                    bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50`}
                >
                  <CardContent>
                    {/* Title + Grievance Id */}
                    <div className="flex justify-between items-start mb-2">
                      <div className="flex-1">
                        <h4 className="font-semibold text-gray-900 text-sm mb-1">
                          {g.title || "Untitled"}
                        </h4>
                        <p className="text-xs text-gray-600">
                          Reported by: {g.reported_by || "Unknown"}
                        </p>
                      </div>
                      <span className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                        {g.g_id || "N/A"}
                      </span>
                    </div>

                    {/* Priority + Date */}
                    <div className="flex items-center justify-between">
                      <span
                        className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                          g.priority
                        )}`}
                      >
                        {g.priority
                          ? g.priority.charAt(0).toUpperCase() +
                            g.priority.slice(1)
                          : "N/A"}
                      </span>
                      <div className="flex items-center gap-1 text-xs text-gray-500">
                        <Calendar size={12} />
                        {g.createdAt
                          ? format(new Date(g.createdAt), "MMM dd")
                          : "--"}
                      </div>
                    </div>

                    {/* Category */}
                    <div className="mt-2 text-xs text-gray-600">
                      Category: {g.cat_name || "N/A"}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>

          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center p-4 border-t bg-gray-50 rounded-b-3xl">
              <button
                onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
                disabled={currentPage === 1}
                className="px-3 py-1 text-sm rounded bg-black text-white disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-sm text-gray-700">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() =>
                  setCurrentPage((p) => Math.min(p + 1, totalPages))
                }
                disabled={currentPage === totalPages}
                className="px-3 py-1 text-sm rounded bg-black text-white disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

    {selectedGrievance && (
        <InfoWindow
            position={{
            lat: selectedGrievance.geometry.coordinates[1],
            lng: selectedGrievance.geometry.coordinates[0],
            }}
            onCloseClick={() => setSelectedGrievance(null)}
        >
            <div className="max-w-xs">
            <Card
                className={`shadow-[0_4px_16px_0_rgba(31,38,135,0.15)] 
                relative overflow-hidden rounded-2xl border backdrop-blur-xl
                border-white/30 bg-gradient-to-br from-blue-50 via-yellow-100/20 to-emerald-50`}
            >
                <CardContent>
                {(() => {
                    const g = selectedGrievance.properties || {};
                    return (
                    <>
                        {/* Title + Grievance Id */}
                        <div className="flex justify-between items-start">
                        <div className="flex-1">
                            <h4 className="font-semibold text-gray-900 text-sm mb-1">
                            {g.title || "Untitled"}
                            </h4>
                            <p className="text-xs text-gray-600">
                            Reported by: {g.reported_by || "Unknown"}
                            </p>
                        </div>
                        <span className="text-xs font-mono text-blue-600 bg-blue-50 px-2 py-1 rounded">
                            {g.g_id || "N/A"}
                        </span>
                        </div>

                        {/* Priority + Date */}
                        <div className="flex items-center justify-between">
                        <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityColor(
                            g.priority
                            )}`}
                        >
                            {g.priority
                            ? g.priority.charAt(0).toUpperCase() +
                                g.priority.slice(1)
                            : "N/A"}
                        </span>
                        <div className="flex items-center gap-1 text-xs text-gray-500">
                            <Calendar size={12} />
                            {g.createdAt ? format(new Date(g.createdAt), "MMM dd") : "--"}
                        </div>
                        </div>

                        {/* Category */}
                        <div className="mt-2 text-xs text-gray-600">
                        Category: {g.cat_name || "N/A"}
                        </div>
                    </>
                    );
                })()}
                </CardContent>
            </Card>
            </div>
        </InfoWindow>
    )}
    
      {/* Dashboard Placeholder */}
      {/* <div className="absolute top-4 left-4 bg-white/80 backdrop-blur-xl p-4 rounded-2xl shadow-md">
        <h2 className="text-lg font-bold text-[#004877]">GMS Dashboard</h2>
        <p className="text-sm text-gray-700">Coming soon...</p>
      </div> */}
      <GrievanceSummary grievances={grievances} />

      {!activeCluster && (
        <div className="absolute top-[50%] right-4 z-[20]">
          <Tooltip title={showStatsPanel ? "Hide" : "Show Dashboard"}>
            <Button
                type="default"
                className="bg-white text-black border-none rounded-xl shadow-lg transition-all duration-300 ease-in-out hover:scale-110 flex items-center justify-center w-12 h-12"
                onClick={() => setShowStatsPanel(!showStatsPanel)}
                icon={showStatsPanel ? <AlignLeft size={25} /> : <AlignRight size={25} />}
            />
        </Tooltip>
        </div>
      )}
        {!activeCluster && showStatsPanel && (
        <GrievanceStatsPanel
          grievances={grievances}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
        />
      )}
    </>
  );
};

export default GmsFullScreenMap;
